import React, { useState, useRef, useEffect } from 'react';
import './PDFUpload.css';

const PDFUpload = ({ onUpload, onCancel, isProcessing, type = 'recipe' }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const fileDataRef = useRef(null);

  // Restore file from ref if component remounts (Android issue)
  useEffect(() => {
    if (fileDataRef.current && !selectedFile) {
      setSelectedFile(fileDataRef.current);
    }
  }, [selectedFile]);

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/pdf') {
      // Store in ref to survive component remounts
      fileDataRef.current = file;
      setSelectedFile(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[PDFUpload] File input change triggered');
    console.log('[PDFUpload] Files:', e.target.files);
    
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('[PDFUpload] File selected:', file.name, file.type, file.size);
      
      // Create a copy of the file to prevent it from being garbage collected
      const fileBlob = new Blob([file], { type: file.type });
      const newFile = new File([fileBlob], file.name, { type: file.type });
      
      console.log('[PDFUpload] File copy created, calling handleFileSelect');
      handleFileSelect(newFile);
    } else {
      console.log('[PDFUpload] No file selected');
    }
    
    // Don't reset input value - this might be causing the issue on Android
    // if (e.target) {
    //   e.target.value = '';
    // }
  };

  const handleUpload = () => {
    if (selectedFile && onUpload) {
      onUpload(selectedFile);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    fileDataRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="pdf-upload-container">
      <div className="pdf-upload-header">
        <h3>Upload PDF {type === 'recipe' ? 'Recipe' : 'Cocktail Recipe'}</h3>
        <p>The website blocks automated access. Upload a PDF of the {type} instead.</p>
      </div>

      <div 
        className={`pdf-upload-area ${dragActive ? 'drag-active' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handleFileInputChange}
          onClick={(e) => {
            // Prevent event bubbling on Android
            e.stopPropagation();
          }}
          onFocus={(e) => {
            // Prevent losing focus on Android
            e.stopPropagation();
          }}
          style={{ display: 'none' }}
          disabled={isProcessing}
          capture={false}
          multiple={false}
        />

        {!selectedFile ? (
          <div className="pdf-upload-prompt">
            <div className="pdf-upload-icon">📄</div>
            <p>Drag and drop a PDF file here, or click to select</p>
            <p className="pdf-upload-hint">
              Tip: Print the {type} webpage to PDF (Ctrl+P → Save as PDF)
            </p>
          </div>
        ) : (
          <div className="pdf-upload-selected">
            <div className="pdf-file-info">
              <div className="pdf-file-icon">📄</div>
              <div className="pdf-file-details">
                <p className="pdf-file-name">{selectedFile.name}</p>
                <p className="pdf-file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button 
                className="pdf-remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveFile();
                }}
                disabled={isProcessing}
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="pdf-upload-actions">
        <button 
          className="pdf-cancel-button"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button 
          className="pdf-upload-button"
          onClick={handleUpload}
          disabled={!selectedFile || isProcessing}
        >
          {isProcessing ? 'Processing...' : `Extract ${type === 'recipe' ? 'Recipe' : 'Cocktail'}`}
        </button>
      </div>

      <div className="pdf-upload-instructions">
        <h4>How to create a PDF:</h4>
        <ol>
          <li>Open the {type} webpage in your browser</li>
          <li>Press <kbd>Ctrl+P</kbd> (or <kbd>Cmd+P</kbd> on Mac)</li>
          <li>Select "Save as PDF" as the destination</li>
          <li>Click "Save" and upload the PDF here</li>
        </ol>
      </div>
    </div>
  );
};

export default PDFUpload;